import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, MessageCircle, Target, Clock, Euro, Award, CheckCircle, ArrowRight, Sparkles } from 'lucide-react';

const CoachingPage = () => {
  const benefits = [
    'Dvejojate dėl svarbių sprendimų',
    'Nuolat nepasiryžtate žengti pirmo žingsnio',
    'Jums trūksta motyvacijos ar jaučiatės „įstrigę"',
    'Nežinote, ką daryti toliau savo darbe ar gyvenime',
    'Susiduriate su nedidelėmis, bet nuolat erzinančiomis problemomis',
    'Norite tapti geriausia savo versija ir labiau pasitikėti savimi'
  ];

  const whyChooseMe = [
    { text: 'ICC sertifikuota koučingo specialistė', color: 'bg-[#E8F5E9] text-[#4CAF50]' },
    { text: 'Daugiau nei 10 metų patirtis vedant mokymus ir konsultuojant', color: 'bg-[#FFF3E0] text-[#FF9F43]' },
    { text: 'Vadovaujuosi pasitikėjimo ir pagarbos principais', color: 'bg-[#E3F2FD] text-[#5B9BD5]' },
    { text: 'Tikiu, kad kiekvienas turi potencialą kurti prasmės turinčius pokyčius', color: 'bg-[#F3E5F5] text-[#9B59B6]' }
  ];

  return (
    <div data-testid="coaching-page" className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-[#FAFAF8] py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center space-x-2 bg-[#FFF3E0] text-[#FF9F43] px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Heart className="w-4 h-4" />
                <span>Individualios sesijos</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl font-['Poppins'] font-bold leading-tight text-[#2D3748] mb-6">
                Asmeninė <span className="text-[#FF9F43]">Koučingo Sesija</span>
              </h1>
              <p className="text-lg text-[#718096] leading-relaxed mb-8">
                Koučingo sesija – tai ugdomoji konsultacija, kurios metu taikant specialią klausimų / užduočių techniką klientas randa sprendimus, susikuria daugiau aiškumo ir ramybės bei džiaugsmo savo gyvenime ir darbe.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/kontaktai"
                  data-testid="cta-book-session"
                  className="inline-flex items-center justify-center bg-[#FF9F43] text-white hover:bg-[#E08A3A] rounded-full px-8 py-4 font-semibold shadow-lg shadow-orange-200 transition-all hover:-translate-y-1"
                >
                  Užsisakyti sesiją
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </div>
            </div>

            <div className="relative">
              <div className="bg-white rounded-3xl p-8 shadow-xl border border-slate-100">
                <div className="space-y-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 rounded-full bg-[#E8F5E9] flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-[#4CAF50]" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#2D3748]">Trukmė</p>
                      <p className="text-[#718096]">1 valanda</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 rounded-full bg-[#FFF3E0] flex items-center justify-center flex-shrink-0">
                      <MessageCircle className="w-6 h-6 text-[#FF9F43]" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#2D3748]">Forma</p>
                      <p className="text-[#718096]">Nuotoliniu būdu</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 rounded-full bg-[#E3F2FD] flex items-center justify-center flex-shrink-0">
                      <Euro className="w-6 h-6 text-[#5B9BD5]" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#2D3748]">Kaina</p>
                      <p className="text-[#718096]">40 Eur / 1 val. sesija</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-20 h-20 bg-[#FFD93D] rounded-full opacity-60 -z-10"></div>
              <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-[#5B9BD5] rounded-full opacity-60 -z-10"></div>
            </div>
          </div>
        </div>
      </section>

      {/* When It's Good For You */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-['Poppins'] font-bold text-[#2D3748] mb-4 text-center">
            Kam puikiai tinka koučingas?
          </h2>
          <p className="text-lg text-[#718096] mb-12 text-center">
            Koučingo sesija puikiai tinka, jei:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start space-x-3 bg-[#FAFAF8] p-6 rounded-2xl hover:bg-[#FFF3E0] transition-colors">
                <CheckCircle className="w-6 h-6 text-[#FF9F43] flex-shrink-0 mt-0.5" />
                <span className="text-[#718096]">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-[#FAFAF8] py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-['Poppins'] font-bold text-[#2D3748] mb-12 text-center">
            Kaip veikia koučingas?
          </h2>

          <div className="bg-white rounded-3xl p-8 md:p-12 border border-slate-100 mb-8">
            <h3 className="text-2xl font-['Poppins'] font-semibold text-[#2D3748] mb-4">
              Koučingo esmė
            </h3>
            <p className="text-lg text-[#718096] leading-relaxed mb-6">
              Koučingo esmė slypi klausimuose. Pagal koučingo filosofiją, kiekvienas žmogus jau turi savyje atsakymus į svarbiausius klausimus – tik kartais reikia pagalbos juos atrasti. Mano, kaip koučerės, užduotis yra padėti jums pažvelgti į situaciją iš įvairių perspektyvų, atrasti ribojančius įsitikinimus ir nuostatas bei atverti duris naujoms galimybėms.
            </p>
            
            <div className="bg-[#E8F5E9] rounded-2xl p-6 border-l-4 border-[#4CAF50]">
              <p className="text-lg text-[#2D3748] font-medium">
                Koučingas nėra patarimai ar sprendimų siūlymas.
              </p>
              <p className="text-[#718096] mt-2">
                Vietoj to, koučeris užduoda tinkamus klausimus, parodydamas pasitikėjimą jūsų gebėjimais ir pagarba jūsų unikalumui. Kai atrandate atsakymus patys, tai suteikia daugiau pasitikėjimo savimi ir užtikrina, kad pokyčiai būtų ilgalaikiai.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-3xl p-8 md:p-12 border border-slate-100">
            <h3 className="text-2xl font-['Poppins'] font-semibold text-[#2D3748] mb-6">
              Kaip vyksta sesija?
            </h3>
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 rounded-full bg-[#4CAF50] text-white flex items-center justify-center flex-shrink-0 font-bold">
                  1
                </div>
                <div>
                  <h4 className="font-semibold text-[#2D3748] mb-2">Pirmoji sesija</h4>
                  <p className="text-[#718096]">Aptariame jūsų poreikius ir tikslus. Susipažįstame ir nustatome darbo kryptį.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 rounded-full bg-[#FF9F43] text-white flex items-center justify-center flex-shrink-0 font-bold">
                  2
                </div>
                <div>
                  <h4 className="font-semibold text-[#2D3748] mb-2">Tolimesnės sesijos</h4>
                  <p className="text-[#718096]">Kiekvienas pokalbis pritaikomas individualiai, atsižvelgiant į jūsų situaciją.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 rounded-full bg-[#5B9BD5] text-white flex items-center justify-center flex-shrink-0 font-bold">
                  3
                </div>
                <div>
                  <h4 className="font-semibold text-[#2D3748] mb-2">Sesijų skaičius</h4>
                  <p className="text-[#718096]">Kai kurie klientai sprendimus randa jau po 1–2 susitikimų, tačiau sudėtingesniems klausimams prireikia vidutiniškai 5 sesijų.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-['Poppins'] font-bold text-[#2D3748] mb-12 text-center">
            Kaina
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-3xl p-8 border-2 border-slate-200 hover:border-[#FF9F43] transition-colors">
              <div className="text-center">
                <h3 className="text-2xl font-['Poppins'] font-semibold text-[#2D3748] mb-4">
                  Viena sesija
                </h3>
                <div className="text-5xl font-bold text-[#FF9F43] mb-2">
                  40 <span className="text-2xl">€</span>
                </div>
                <p className="text-[#718096] mb-6">Už 1 valandos sesiją</p>
                <Link
                  to="/kontaktai"
                  className="inline-block w-full bg-[#2D3748] text-white hover:bg-[#1a202c] rounded-full px-6 py-3 font-semibold transition-colors"
                >
                  Užsisakyti
                </Link>
              </div>
            </div>

            <div className="bg-[#FF9F43] rounded-3xl p-8 text-white relative overflow-hidden">
              <div className="absolute top-4 right-4 bg-white/20 px-3 py-1 rounded-full text-xs font-semibold">
                Populiariausias
              </div>
              <div className="text-center relative z-10">
                <h3 className="text-2xl font-['Poppins'] font-semibold mb-4">
                  5 sesijų paketas
                </h3>
                <div className="text-5xl font-bold mb-2">
                  Kontaktas
                </div>
                <p className="text-white/90 mb-6">Individualiai aptariama kaina</p>
                <Link
                  to="/kontaktai"
                  className="inline-block w-full bg-white text-[#FF9F43] hover:bg-slate-100 rounded-full px-6 py-3 font-semibold transition-colors"
                >
                  Susisiekti
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Me */}
      <section className="bg-[#FAFAF8] py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-['Poppins'] font-bold text-[#2D3748] mb-8 text-center">
            Kodėl verta rinktis mane?
          </h2>
          
          <div className="bg-white rounded-3xl p-8 md:p-12 border border-slate-100">
            <div className="flex flex-wrap gap-3 mb-8 justify-center">
              {whyChooseMe.map((reason, index) => (
                <span key={index} className={`${reason.color} px-4 py-2 rounded-full text-sm font-medium`}>
                  {reason.text}
                </span>
              ))}
            </div>
            <p className="text-lg text-[#718096] leading-relaxed text-center">
              Mano misija – padėti jums atrasti savo stiprybes, įveikti kasdienius iššūkius ir pasiekti didesnį pasitenkinimą gyvenime ir darbe. Dirbdama vadovaujuosi pasitikėjimo ir pagarbos principais, nes tikiu, kad kiekvienas žmogus turi potencialą kurti prasmės turinčius pokyčius savo gyvenime.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#FF9F43] py-16">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl text-center">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Heart className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-3xl md:text-4xl font-['Poppins'] font-bold text-white mb-6">
            Pradėkime jūsų kelionę
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Užsisakykite pirmąją koučingo sesiją ir ženkite link savo tikslo
          </p>
          <Link
            to="/kontaktai"
            data-testid="cta-footer-button"
            className="inline-flex items-center bg-white text-[#FF9F43] hover:bg-slate-100 rounded-full px-8 py-4 font-semibold transition-colors"
          >
            Užsisakyti sesiją
            <ArrowRight className="w-5 h-5 ml-2" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CoachingPage;
